/**
 * <dev-remark> Custom Element
 *
 * The version badge rendered next to the logo on the password-gated staging
 * store (`3sixteen-dev-2`). Clicking it copies a shareable preview link to the
 * clipboard: the current page URL with `preview_theme_id` attached so a
 * teammate opens the exact same page on the exact same preview theme, then
 * flashes a confirmation notice.
 *
 * The badge lives inside the logo `<a>`, so the click handler cancels the
 * anchor's navigation (`preventDefault`) AND stops the event from reaching
 * Turbo's document-level click delegate (`stopPropagation`) — otherwise the
 * click would navigate to the shop root instead of copying.
 *
 * Only rendered on staging (host gate in header.liquid), so on production the
 * tag never appears and this element never upgrades.
 *
 * The badge sits inside `#header-global`, which is `data-turbo-permanent` —
 * Turbo transplants the same element instance across navigations (see
 * CLAUDE.md "Custom element inside a permanent region"). The click listener is
 * bound to the element itself, so it travels with the transplanted node;
 * re-binding on reconnect uses the same handler reference and dedupes to a
 * no-op, so no guard/teardown dance is needed.
 *
 * Source path: theme/js/components/dev-remark.esm.js
 * Built name : ./dev-remark.js
 *
 * AI: Entirely written by AI
 * Date: 2026-09-12
 * Model: Claude Opus 4.8
 *
 * [DevRemark]
 */

import { log } from './log.js';
import { push as notifierPush } from './notifier.js';

// Resolve the preview theme id to attach to the shared link.
//   1. `preview_theme_id` already in the page URL — most explicit; use as-is.
//   2. `window.ep_appinfo.theme.id` — hydrated by content-for-footer.liquid
//      from Shopify Liquid's `theme` global. Covers the common case where the
//      dev navigated via a relative link that dropped the query param.
// Mirrors the two-signal precedence in core/sections.esm.js's getPreviewThemeId.
function resolvePreviewThemeId() {
  const match = window.location.search.match(/[?&]preview_theme_id=(\d+)/);
  if (match) return match[1];
  const info = window.ep_appinfo;
  if (info && info.theme && info.theme.id) return String(info.theme.id);
  return null;
}

// Build the shareable link: the current page with `preview_theme_id` ensured,
// plus an `#ep_return=<path>` fragment so the destination survives the
// storefront password gate. Shopify's password form always redirects to `/`
// (losing the path), but a URL fragment survives the redirect *to* /password —
// preview-return.esm.js reads it there, stashes it, and restores it after auth.
// The fragment is harmless for a recipient who already holds the password
// cookie (they land on the link directly and preview-return just strips it).
function buildPreviewLink() {
  const url = new URL(window.location.href);
  const previewId = resolvePreviewThemeId();
  if (previewId) {
    url.searchParams.set('preview_theme_id', previewId);
  }
  const relative = url.pathname + url.search;
  return url.origin + relative + '#ep_return=' + encodeURIComponent(relative);
}

async function copyToClipboard(text) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      log('[DevRemark] clipboard API failed, falling back', err);
    }
  }
  // Fallback for insecure contexts / older browsers where the async
  // Clipboard API is unavailable or blocked.
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch (err) {
    log('[DevRemark] clipboard fallback failed', err);
    return false;
  }
}

class DevRemark extends HTMLElement {
  connectedCallback() {
    // Bound reference is stable, so a re-add on Turbo reconnect is a no-op.
    this.addEventListener('click', this._onActivate);
    this.addEventListener('keydown', this._onKeydown);
  }

  _onActivate = (event) => {
    // The badge lives inside the logo <a>; suppress both the native anchor
    // navigation and Turbo's document-level click handler.
    event.preventDefault();
    event.stopPropagation();
    this._copyPreviewLink();
  };

  _onKeydown = (event) => {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      this._onActivate(event);
    }
  };

  async _copyPreviewLink() {
    const link = buildPreviewLink();
    log('[DevRemark] copying preview link ' + link);
    const copied = await copyToClipboard(link);
    notifierPush({
      type: 'flash',
      content: copied ? 'Copied preview link to clipboard' : 'Could not copy preview link',
      force: true,
    });
  }
}

customElements.define('dev-remark', DevRemark);
log('[DevRemark] registered');

export { DevRemark };
